import { createOptimisticComment } from '../utils/createOptimisticComment.js';
import { useMutation } from '@tanstack/react-query';
import { useQueryCacheCommentHelpers } from '../utils/useQueryCacheCommentHelpers.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { useWoody } from '../../woody/woody.js';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { toast } from '../../store/alerts.js';
import { dispatchFabricEvent } from '../../hooks/useFabricEvent.js';
import { omit } from '@fabric-app/utils/object/manipulation';

const useMutationCreateComment = () => {
  const { data: user } = useQueryUserInfo();
  const { client } = useWoody();
  const { addCachedComment, replaceCommentInCache } = useQueryCacheCommentHelpers();
  const accessToken = useContextAccessToken();
  const mutation = useMutation({
    mutationFn: async ({ resourceId, content, annotation }) => {
      return client.v2({
        endpoint: "/v2/resources/{resourceId}/comments",
        params: {
          resourceId
        }
      }, {
        query: {
          accessToken
        },
        method: "post",
        body: {
          content,
          annotation: annotation ? omit(annotation, [
            "id"
          ]) : void 0
        }
      });
    },
    onMutate: ({ resourceId, content }) => {
      if (!user) return;
      const optimisticComment = createOptimisticComment(user, content, resourceId);
      return {
        optimisticComment,
        ...addCachedComment(optimisticComment)
      };
    },
    onError: (_error, _variables, context) => {
      toast({
        content: "Failed to create comment"
      });
      context?.resetCacheToPreOptimisticState();
      context?.invalidateQueries();
    },
    onSuccess: (data, variables, context) => {
      replaceCommentInCache(context.optimisticComment, data);
      if (variables.annotation && data.id && data.annotation) {
        dispatchFabricEvent("fabric-annotation-action-link", {
          type: "link-annotation",
          annotation: variables.annotation,
          commentId: data.id,
          resourceId: variables.resourceId
        });
      }
    }
  });
  return mutation;
};

export { useMutationCreateComment };
//# sourceMappingURL=useMutationCreateComment.js.map
